// User Data Persistence - Facade for backward compatibility
// Delegates to modular storage services while maintaining original API

// Storage Detection & Environment
export {
  shouldDisableEncryption,
  detectStorageEnvironment,
  getStorageAvailability,
  clearStorageAvailabilityCache,
} from './storage/storageDetection';

// Storage Helpers & Core Operations
export {
  USER_DATA_KEY,
  localStorageHelpers,
  sessionStorageHelpers,
  cookieHelpers,
  urlParamHelpers,
  inMemoryHelpers,
  readFromStorage,
  writeToStorage,
  removeFromStorage,
} from './storage/storageHelpers';

// Encryption Service
export {
  getEncryptionKey,
  encryptValue,
  decryptValue,
  clearDecryptionCache,
  getEncryptionStrategy,
} from './storage/encryptionService';

// User Data CRUD Operations
export {
  storeUserData,
  getUserData,
  clearUserData,
  removeUserDataFields,
  getHashedUserData,
  clearPerformanceCache,
} from './storage/userDataService';

// Purchase State Management
export {
  getPurchaseState,
  updatePurchaseState,
  clearSensitivePurchaseData,
  clearAllPurchaseState,
  clearOTOPurchaseState,
  getPurchaseSummary,
  markInitialPurchaseCompleted,
  getPurchaseStateWithExpiringFields,
} from './storage/purchaseStateService';

// Storage Utils & Diagnostics
export {
  getStorageDiagnostics,
  clearStorageKeys,
  emergencyDisableEncryption,
  emergencyEnableEncryption,
  reportEncryptionStrategy,
  clearAllEncryptionKeys,
  getStorageSystemStatus,
} from './storage/storageUtils';

// Cache Management
export { clearAllCaches } from './storage/index';

// Analytics Queue Management
export { clearAnalyticsQueues } from '../components/analytics/queueManagement';

/**
 * Complete system reset - clears everything and all encryption keys
 */
export const emergencySystemReset = () => {
  try {
    import('./storage/storageHelpers').then(
      ({ removeFromStorage, USER_DATA_KEY }) => {
        removeFromStorage(USER_DATA_KEY);
      }
    );
    import('./storage/storageUtils').then(({ clearAllEncryptionKeys }) => {
      clearAllEncryptionKeys();
    });
    import('./storage/encryptionService').then(({ clearDecryptionCache }) => {
      clearDecryptionCache();
    });

    console.warn(
      '🚨 EMERGENCY: Complete system reset performed. All data cleared.'
    );
  } catch (error) {
    console.error('Failed to perform emergency system reset:', error);
  }
};

// Default export for backward compatibility
const userDataPersistence = {
  shouldDisableEncryption: () =>
    import('./storage/storageDetection').then(m => m.shouldDisableEncryption()),
  detectStorageEnvironment: () =>
    import('./storage/storageDetection').then(m =>
      m.detectStorageEnvironment()
    ),
  getStorageAvailability: () =>
    import('./storage/storageDetection').then(m => m.getStorageAvailability()),
  storeUserData: data =>
    import('./storage/userDataService').then(m => m.storeUserData(data)),
  getUserData: params =>
    import('./storage/userDataService').then(m => m.getUserData(params)),
  clearUserData: () =>
    import('./storage/userDataService').then(m => m.clearUserData()),
  getPurchaseState: () =>
    import('./storage/purchaseStateService').then(m => m.getPurchaseState()),
  updatePurchaseState: updates =>
    import('./storage/purchaseStateService').then(m =>
      m.updatePurchaseState(updates)
    ),
  getStorageDiagnostics: () =>
    import('./storage/storageUtils').then(m => m.getStorageDiagnostics()),
  emergencyDisableEncryption: () =>
    import('./storage/storageUtils').then(m => m.emergencyDisableEncryption()),
};

export default userDataPersistence;
